import React, { useState, useEffect } from 'react';
import { Question } from '../types/Survey';
import { renderCategoryIcon } from '../utils/categoryIcons';
import { useAuth } from '../context/AuthContext';
import { v4 as uuidv4 } from 'uuid';
import ConditionalLogicBuilder from './ConditionalLogicBuilder';
import {
  Shuffle,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,

  Save,
  FileText,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader } from './ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import './SurveyBuilder.css';

interface TemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (template: any) => void;
  template?: any;
  categories: any[];
  mode: 'create' | 'edit';
}

const TemplateModal: React.FC<TemplateModalProps> = ({
  isOpen,
  onClose,
  onSave,
  template,
  categories,
  mode
}) => {
  const { user, centerInfo, selectedCenterId } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [estimatedTime, setEstimatedTime] = useState(10);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [questions, setQuestions] = useState<Question[]>([]);
  const [showConditionalLogic, setShowConditionalLogic] = useState<{
    [questionId: string]: boolean;
  }>({});
  const [movingQuestions, setMovingQuestions] = useState<{
    [questionId: string]: "up" | "down" | null;
  }>({});

  useEffect(() => {
    if (template && mode === 'edit') {
      setName(template.name || '');
      setDescription(template.description || '');
      setCategoryId(template.categoryId || template.category || '');
      setEstimatedTime(template.estimatedTime || 10);
      setTags(template.tags || []);
      // ID가 없는 질문들에 ID 부여
      const questionsWithIds = (template.questions || []).map((q: any) => ({
        ...q,
        id: q.id || uuidv4(),
      }));
      setQuestions(questionsWithIds);
    } else if (template && mode === 'create') {
      // Create mode with default category
      setName('');
      setDescription('');
      setCategoryId(template.category || '');
      setEstimatedTime(10);
      setTags([]);
      setQuestions([]);
    } else {
      // Reset form for create mode without default
      setName('');
      setDescription('');
      setCategoryId('');
      setEstimatedTime(10);
      setTags([]);
      setQuestions([]);
    }
    setShowConditionalLogic({});
    setMovingQuestions({});
  }, [template, mode, isOpen]);

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (index: number) => {
    setTags(tags.filter((_, i) => i !== index));
  };

  const addQuestion = () => {
    const newQuestion: Question = {
      id: uuidv4(),
      type: "multiple-choice",
      question: "",
      options: ["옵션 1", "옵션 2"],
      required: false,
    };
    setQuestions([...questions, newQuestion]);
  };

  const updateQuestion = (id: string, updates: Partial<Question>) => {
    setQuestions(
      questions.map((q) => {
        if (q.id === id) {
          const updatedQuestion = { ...q, ...updates };

          // 질문 타입 변경 시 기본값 설정
          if (updates.type) {
            switch (updates.type) {
              case "multiple-choice":
              case "checkbox":
              case "dropdown":
                if (!updatedQuestion.options) {
                  updatedQuestion.options = ["옵션 1", "옵션 2"];
                }
                break;
              case "rating":
                if (!updatedQuestion.minRating) updatedQuestion.minRating = 1;
                if (!updatedQuestion.maxRating) updatedQuestion.maxRating = 5;
                break;
              case "matrix":
                if (!updatedQuestion.matrixRows)
                  updatedQuestion.matrixRows = ["행 1", "행 2"];
                if (!updatedQuestion.matrixColumns)
                  updatedQuestion.matrixColumns = ["열 1", "열 2"];
                if (!updatedQuestion.matrixType)
                  updatedQuestion.matrixType = "radio";
                break;
              case "ranking":
                if (!updatedQuestion.rankingItems)
                  updatedQuestion.rankingItems = ["항목 1", "항목 2", "항목 3"];
                break;
              case "file-upload":
                if (!updatedQuestion.allowedFileTypes)
                  updatedQuestion.allowedFileTypes = [];
                if (!updatedQuestion.maxFileSize)
                  updatedQuestion.maxFileSize = 10;
                break;
            }
          }

          return updatedQuestion;
        }
        return q;
      })
    );
  };

  const deleteQuestion = (id: string) => {
    setQuestions(questions.filter((q) => q.id !== id));
  };

  const moveQuestionUp = (index: number) => {
    if (index <= 0) return;

    const questionId = questions[index].id;
    const prevQuestionId = questions[index - 1].id;

    // Set animation states
    setMovingQuestions({
      [questionId]: "up",
      [prevQuestionId]: "down",
    });

    // Perform the swap after a short delay to show animation
    setTimeout(() => {
      const newQuestions = [...questions];
      [newQuestions[index - 1], newQuestions[index]] = [
        newQuestions[index],
        newQuestions[index - 1],
      ];
      setQuestions(newQuestions);

      // Clear animation states
      setTimeout(() => {
        setMovingQuestions({});
      }, 300);
    }, 50);
  };

  const moveQuestionDown = (index: number) => {
    if (index >= questions.length - 1) return;

    const questionId = questions[index].id;
    const nextQuestionId = questions[index + 1].id;

    // Set animation states
    setMovingQuestions({
      [questionId]: "down",
      [nextQuestionId]: "up",
    });

    // Perform the swap after a short delay to show animation
    setTimeout(() => {
      const newQuestions = [...questions];
      [newQuestions[index], newQuestions[index + 1]] = [
        newQuestions[index + 1],
        newQuestions[index],
      ];
      setQuestions(newQuestions);

      // Clear animation states
      setTimeout(() => {
        setMovingQuestions({});
      }, 300);
    }, 50);
  };

  const addOption = (questionId: string) => {
    const question = questions.find((q) => q.id === questionId);
    if (question?.options) {
      const newOptions = [
        ...question.options,
        `옵션 ${question.options.length + 1}`,
      ];
      updateQuestion(questionId, { options: newOptions });
    }
  };

  const updateOption = (
    questionId: string,
    optionIndex: number,
    value: string
  ) => {
    const question = questions.find((q) => q.id === questionId);
    if (question?.options) {
      const newOptions = [...question.options];
      newOptions[optionIndex] = value;
      updateQuestion(questionId, { options: newOptions });
    }
  };

  const deleteOption = (questionId: string, optionIndex: number) => {
    const question = questions.find((q) => q.id === questionId);
    if (question?.options && question.options.length > 2) {
      const newOptions = question.options.filter(
        (_, index) => index !== optionIndex
      );
      updateQuestion(questionId, { options: newOptions });
    }
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      alert('템플릿 이름을 입력해주세요');
      return;
    }
    if (!categoryId) {
      alert('카테고리를 선택해주세요');
      return;
    }
    if (questions.length === 0) {
      alert('최소 1개 이상의 질문을 추가해주세요');
      return;
    }

    const templateData = {
      ...(template?.id && { id: template.id }),
      name,
      description,
      categoryId,
      category: categoryId,  // 백엔드 호환을 위해 category 필드도 추가
      estimatedTime,
      tags,
      questions,
      isActive: true,
      createdById: String(user?.userid || user?.email || 'external-user'),
      centerUserId: selectedCenterId || user?.centerId || centerInfo?.centerId || null
    };

    onSave(templateData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <FileText size={24} />
              {mode === 'create' ? '새 템플릿 만들기' : '템플릿 수정'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {mode === 'create' ? '새로운 설문 템플릿을 생성하세요' : '기존 템플릿을 수정하세요'}
            </p>
          </div>
          <div className="flex gap-4">
            <Button variant="secondary" onClick={onClose}>
              취소
            </Button>
            <Button onClick={handleSubmit}>
              <Save className="mr-2 h-4 w-4" />
              {mode === 'create' ? '템플릿 생성' : '템플릿 수정'}
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-6 space-y-6">
          {/* 기본 정보 */}
          <Card>
            <CardContent className="p-6">
              <Input
                type="text"
                placeholder="템플릿 이름을 입력하세요"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mb-4 text-lg"
              />
              
              <div className="flex gap-4 mb-4">
                <Select value={categoryId} onValueChange={setCategoryId}>
                  <SelectTrigger className="w-[250px]">
                    <SelectValue placeholder="카테고리 선택" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        <span className="flex items-center gap-2">
                          {renderCategoryIcon(category.icon)} {category.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Input
                  type="number"
                  placeholder="소요 시간 (분)"
                  value={estimatedTime}
                  onChange={(e) => setEstimatedTime(parseInt(e.target.value) || 10)}
                  min="1"
                  className="w-[150px]"
                />
              </div>

              <Textarea
                placeholder="템플릿 설명 (선택사항)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="min-h-[80px] mb-4"
              />

              {/* 태그 입력 */}
              <div className="space-y-2">
                <Label>태그</Label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="태그를 입력하고 Enter를 누르세요"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    className="flex-1"
                  />
                  <Button type="button" onClick={handleAddTag} variant="outline">
                    추가
                  </Button>
                </div>
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {tags.map((tag, index) => (
                      <Badge key={index} variant="secondary" className="gap-1">
                        {tag}
                        <button
                          onClick={() => handleRemoveTag(index)}
                          className="ml-1 text-xs hover:text-red-500"
                        >
                          ×
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* 질문 섹션 */}
          <div className="space-y-6">
            {questions.map((question, index) => (
              <Card
                key={question.id}
                className={`mb-6 transition-all duration-300 ease-in-out ${
                  movingQuestions[question.id] === "up"
                    ? "question-card-moving-up"
                    : movingQuestions[question.id] === "down"
                    ? "question-card-moving-down"
                    : ""
                }`}
              >
                <CardHeader className="flex flex-row items-center justify-between pb-3">
                  <Badge variant="secondary">질문 {index + 1}</Badge>
                  <div className="flex gap-2">
                    {/* 순서 변경 버튼 */}
                    {index > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => moveQuestionUp(index)}
                        title="위로 이동"
                        className="hover:bg-gray-100"
                      >
                        <ChevronUp size={16} />
                      </Button>
                    )}
                    {index < questions.length - 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => moveQuestionDown(index)}
                        title="아래로 이동"
                        className="hover:bg-gray-100"
                      >
                        <ChevronDown size={16} />
                      </Button>
                    )}
                    {/* 조건부 로직 버튼 */}
                    {index > 0 && questions.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowConditionalLogic((prev) => ({
                            ...prev,
                            [question.id]: !prev[question.id],
                          }));
                        }}
                        title="조건부 로직 설정"
                        className={
                          question.conditionalLogic &&
                          question.conditionalLogic.length > 0
                            ? "bg-green-500 text-white hover:bg-green-600"
                            : ""
                        }
                      >
                        <Shuffle size={16} />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteQuestion(question.id)}
                      title="질문 삭제"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <Input
                    type="text"
                    placeholder="질문을 입력하세요"
                    value={question.question}
                    onChange={(e) =>
                      updateQuestion(question.id, { question: e.target.value })
                    }
                    className="mb-4"
                  />

                  <div className="flex items-center gap-4 mb-4">
                    <Select
                      value={question.type}
                      onValueChange={(value) =>
                        updateQuestion(question.id, {
                          type: value as Question["type"],
                        })
                      }
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="multiple-choice">객관식</SelectItem>
                        <SelectItem value="checkbox">체크박스</SelectItem>
                        <SelectItem value="text">주관식</SelectItem>
                        <SelectItem value="rating">평점</SelectItem>
                        <SelectItem value="dropdown">드롭다운</SelectItem>
                        <SelectItem value="matrix">매트릭스</SelectItem>
                        <SelectItem value="ranking">순위 매기기</SelectItem>
                        <SelectItem value="file-upload">파일 업로드</SelectItem>
                      </SelectContent>
                    </Select>

                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`required-${question.id}`}
                        checked={question.required}
                        onCheckedChange={(checked) =>
                          updateQuestion(question.id, { required: !!checked })
                        }
                      />
                      <Label htmlFor={`required-${question.id}`}>필수 응답</Label>
                    </div>
                  </div>

                  {/* 질문 타입별 설정 */}
                  {(question.type === "multiple-choice" ||
                    question.type === "checkbox" ||
                    question.type === "dropdown") && (
                    <div className="space-y-2">
                      {question.options?.map((option, optionIndex) => (
                        <div key={optionIndex} className="flex items-center gap-2">
                          <Input
                            type="text"
                            value={option}
                            onChange={(e) =>
                              updateOption(question.id, optionIndex, e.target.value)
                            }
                            className="flex-1"
                          />
                          {question.options!.length > 2 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteOption(question.id, optionIndex)}
                              className="text-red-500 hover:text-red-700"
                            >
                              ×
                            </Button>
                          )}
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => addOption(question.id)}
                        className="mt-2"
                      >
                        옵션 추가
                      </Button>
                    </div>
                  )}

                  {question.type === "rating" && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>최소값</Label>
                        <Input
                          type="number"
                          value={question.minRating || 1}
                          onChange={(e) =>
                            updateQuestion(question.id, {
                              minRating: parseInt(e.target.value),
                            })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>최대값</Label>
                        <Input
                          type="number"
                          value={question.maxRating || 5}
                          onChange={(e) =>
                            updateQuestion(question.id, {
                              maxRating: parseInt(e.target.value),
                            })
                          }
                        />
                      </div>
                    </div>
                  )}

                  {/* 조건부 로직 섹션 */}
                  {showConditionalLogic[question.id] && (
                    <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="font-medium text-sm">조건부 로직 설정</h4>
                        {question.conditionalLogic &&
                          question.conditionalLogic.length > 0 && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                updateQuestion(question.id, {
                                  conditionalLogic: [],
                                });
                                setShowConditionalLogic((prev) => ({
                                  ...prev,
                                  [question.id]: false,
                                }));
                              }}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 size={14} className="mr-1" />
                              모든 조건 삭제
                            </Button>
                          )}
                      </div>
                      <ConditionalLogicBuilder
                        question={question}
                        allQuestions={questions}
                        onLogicChange={(logic) =>
                          updateQuestion(question.id, { conditionalLogic: logic })
                        }
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}

            <Button
              variant="secondary"
              onClick={addQuestion}
              className="w-full"
            >
              <Plus size={16} className="mr-2" />
              질문 추가
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateModal;